// src/components/common/AlarmListener.js
// Mendengarkan event "machineReported" dari server (saat user lapor kerusakan),
// lalu membunyikan alarm (siren via Web Audio) + menampilkan popup.
// Alarm berhenti OTOMATIS setelah beberapa detik. Dipasang di dashboard
// admin / maintenance / teknisi. Bisa berbunyi di perangkat apa pun (termasuk
// tablet) yang membuka dashboard & sudah login.
import { useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import api from "../../axiosConfig";
import { enablePush, pushSupported, pushPermission } from "../../push";
import "./AlarmListener.css";

const ALARM_DURATION_MS = 30000; // alarm berbunyi 30 detik lalu berhenti otomatis

export default function AlarmListener({ showTest = false, role }) {
  const [report, setReport] = useState(null); // data laporan utk popup
  const [testing, setTesting] = useState(false);
  const [pushState, setPushState] = useState("default"); // default|granted|denied|working
  const audioCtxRef = useRef(null);
  const oscRef = useRef(null);
  const stopTimerRef = useRef(null);
  const dismissTimerRef = useRef(null);

  // --- AudioContext (kebijakan autoplay: butuh gesture pertama) ---
  const ensureAudioCtx = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audioCtxRef.current = new Ctx();
    }
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    return ctx;
  };

  // Buka audio pada interaksi pertama user (sekali saja) — penting utk tablet kiosk.
  useEffect(() => {
    const unlock = () => ensureAudioCtx();
    const opts = { once: true, passive: true };
    window.addEventListener("click", unlock, opts);
    window.addEventListener("touchstart", unlock, opts);
    window.addEventListener("keydown", unlock, opts);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const stopAlarm = () => {
    if (oscRef.current) {
      try { oscRef.current.stop(); } catch (_) {}
      try { oscRef.current.disconnect(); } catch (_) {}
      oscRef.current = null;
    }
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
  };

  const playSiren = () => {
    const ctx = ensureAudioCtx();
    if (!ctx) return;
    stopAlarm();
    const now = ctx.currentTime;
    const totalSec = ALARM_DURATION_MS / 1000;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.28, now + 0.05);
    gain.connect(ctx.destination);

    const osc = ctx.createOscillator();
    osc.type = "square";
    // efek siren naik-turun 600 <-> 1000 Hz tiap 0.5 dtk
    osc.frequency.setValueAtTime(600, now);
    for (let t = 0.5; t <= totalSec; t += 0.5) {
      const f = Math.round(t / 0.5) % 2 === 0 ? 600 : 1000;
      osc.frequency.linearRampToValueAtTime(f, now + t);
    }
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + totalSec);
    // fade out di akhir agar tidak "klik"
    gain.gain.setValueAtTime(0.28, now + totalSec - 0.15);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + totalSec);

    oscRef.current = osc;
  };

  const triggerAlarm = (data) => {
    setReport(data);
    playSiren();
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    stopTimerRef.current = setTimeout(stopAlarm, ALARM_DURATION_MS);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => setReport(null), ALARM_DURATION_MS + 2000);
  };

  // --- koneksi socket & listener ---
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const socket = io(window.location.origin, {
      // websocket dulu; kalau diblokir proxy hosting -> fallback ke polling
      transports: ["websocket", "polling"],
      auth: { token },
    });

    socket.on("machineReported", triggerAlarm);

    return () => {
      socket.off("machineReported", triggerAlarm);
      socket.disconnect();
      stopAlarm();
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    stopAlarm();
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setReport(null);
  };

  // status izin notifikasi saat mount
  useEffect(() => {
    if (pushSupported()) setPushState(pushPermission());
  }, []);

  const handleEnablePush = async () => {
    setPushState("working");
    try {
      await enablePush(role);
      setPushState("granted");
      alert("✅ Notifikasi HP aktif. Alarm akan masuk walau HP terkunci.");
    } catch (e) {
      setPushState(pushPermission());
      alert("Gagal mengaktifkan notifikasi: " + (e?.message || e));
    }
  };

  const handleTest = async () => {
    ensureAudioCtx(); // klik ini sekaligus "membuka kunci" audio
    setTesting(true);
    try {
      await api.post("/alarm/test");
    } catch (e) {
      // fallback: kalau server tak terjangkau, picu lokal saja
      triggerAlarm({
        machineName: "TES ALARM",
        machineCode: "TEST",
        sectorName: "-",
        issue: "Ini hanya uji coba alarm (lokal)",
        symptom: "-",
        reportedByName: "Teknisi",
        isTest: true,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      {pushSupported() && pushState !== "granted" && (
        <button
          className="alarm-push-btn"
          onClick={handleEnablePush}
          disabled={pushState === "working"}
          title="Aktifkan notifikasi agar alarm masuk walau HP terkunci"
        >
          {pushState === "working" ? "⏳ ..." : "📲 Aktifkan Notifikasi HP"}
        </button>
      )}

      {showTest && (
        <button
          className="alarm-test-btn"
          onClick={handleTest}
          disabled={testing}
          title="Picu alarm uji coba ke semua dashboard"
        >
          🔔 Tes Alarm
        </button>
      )}

      {report && (
        <div className="alarm-overlay" onClick={handleClose}>
          <div className="alarm-card" onClick={(e) => e.stopPropagation()}>
            <div className="alarm-icon">🚨</div>
            <h4 className="alarm-title">
              LAPORAN KERUSAKAN {report.kind === "tools" ? "TOOLS" : "MESIN"}
              {report.isTest && <span className="alarm-test-tag">UJI COBA</span>}
            </h4>
        <div className="alarm-body">
          <div>
            <span>{report.kind === "tools" ? "Tools" : "Mesin"}</span>
            <b>{report.machineName} ({report.machineCode})</b>
          </div>
          <div>
            <span>Sektor</span>
            <b>{report.sectorName}</b>
          </div>
          <div>
            <span>Masalah</span>
            <b>{report.issue}</b>
          </div>
          {report.symptom && report.symptom !== "-" && (
            <div>
              <span>Gejala</span>
              <b>{report.symptom}</b>
            </div>
          )}
          <div>
            <span>Pelapor</span>
            <b>{report.reportedByName}</b>
          </div>
        </div>
            <button className="alarm-btn" onClick={handleClose}>
              Matikan Alarm
            </button>
          </div>
        </div>
      )}
    </>
  );
}
